/**
 * TransferEverythingModal — "Transfer everything from a person" (wireframe admin-console-wireframe; Ownership design,
 * part 4). Pick the current owner (each with how many JDs they own), see what they own (in-review JDs marked), pick the
 * new owner over the whole HR pool with the reasons worked out across ALL of those JDs ("This is the current owner"
 * included), the same carry-over choice (only when one of them has a delegate or shared people), then "Transfer N
 * items". Jobs Library JDs only for now (People + AI is coming soon); archived JDs are not listed (the server skips them
 * too).
 *   open, rows (every JD on the page), transferring, onCancel(), onTransfer({ rows, newOwner, carry, newOwnerName })
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'antd';
import { SbButton, SbModal } from 'uiCore';
import { getTransferCandidates } from 'Api/apiService';
import {
  CANDIDATES_LOAD_FAILED, CARRY, CURRENT_OWNER_LABEL, HIDE_JDS, SHOW_JDS, CURRENT_OWNER_PLACEHOLDER, PICK_CURRENT_FIRST,
  TRANSFER_ALL_TITLE, countPeople, getAdminError, getAffectedNames,
  getAllEligibleHelp, getOwnedRows, getOwnerChoices, getOwnsText, getTransferItemsButton, isInReview, personName,
} from 'Utils/ownershipTransfer';
import { PickerLabelStyled } from 'Components/JdOwnership/PersonPickerModal.styles';
import {
  ConflictItemMetaStyled, ConflictProjectNameStyled,
} from 'Components/EmployeeFunctionAccess/EmployeeFunctionAccess.styles';
import { CarryChoice, NewOwnerField } from './TransferParts';
import {
  CompactItemStyled, FullWidthSelectStyled, InlinePickerLabelStyled, ItemLineStyled, OwnedItemsListStyled,
} from './OwnershipTransfer.styles';

const TransferEverythingModal = ({ open, rows = [], transferring = false, onCancel, onTransfer }) => {
  const [from, setFrom] = useState(null);
  const [newOwner, setNewOwner] = useState(null);
  const [carry, setCarry] = useState(CARRY);
  const [showJds, setShowJds] = useState(false);   // the owned JDs list, collapsed by default
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return;
    setError(null);
    setFrom(null);
    setNewOwner(null);
    setCarry(CARRY);
    setShowJds(false);
    setCandidates(null);
  }, [open]);

  const owned = from ? getOwnedRows(rows, from) : [];
  const choices = getOwnerChoices(rows);
  const fromName = choices.find((c) => c.value === from)?.name || from;

  useEffect(() => {
    if (!open || !from) return undefined;
    let cancelled = false;
    setNewOwner(null);
    setShowJds(false);
    setCandidates(null);
    setError(null);
    setLoading(true);
    getTransferCandidates(getOwnedRows(rows, from).map((r) => r.job_id), from)
      .then((data) => { if (!cancelled) setCandidates(data); })
      .catch((err) => { if (!cancelled) setError(getAdminError(err, CANDIDATES_LOAD_FAILED)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, from]); // eslint-disable-line react-hooks/exhaustive-deps -- rows: the page's, the same while open

  const { ok, total } = countPeople(candidates);
  const ready = Boolean(from && newOwner && owned.length);
  return (
    <SbModal
      open={open}
      title={TRANSFER_ALL_TITLE}
      onCancel={transferring ? undefined : onCancel}
      maskClosable={false}
      destroyOnClose
      width={620}
      // Cancel and the action side by side (the action is disabled until both people are picked)
      footer={[
        <SbButton key="cancel" buttonType="secondary" disabled={transferring} onClick={onCancel}>Cancel</SbButton>,
        <SbButton key="ok" buttonType="primary" disabled={!ready || transferring} loading={transferring}
          onClick={() => onTransfer({ rows: owned, newOwner, carry: carry === CARRY,
            newOwnerName: personName(candidates, newOwner) })}>
          {getTransferItemsButton(owned.length)}
        </SbButton>,
      ]}
    >
      <Flex vertical gap={16}>
        <div>
          <PickerLabelStyled>{CURRENT_OWNER_LABEL}</PickerLabelStyled>
          <FullWidthSelectStyled options={choices} value={from || undefined} onChange={(v) => setFrom(v || null)}
            placeholder={CURRENT_OWNER_PLACEHOLDER} showSearch optionFilterProp="label" allowClear
            disabled={transferring} getPopupContainer={(trigger) => trigger.parentNode}
            aria-label={CURRENT_OWNER_LABEL} />
        </div>
        {from && (
          // One summary line ("X owns N JDs (M in review)") and a link to open the list, so the popup stays short and
          // the New owner dropdown has room; the list, when open, is one compact line per JD and scrolls
          <div data-testid="owned-items">
            <Flex align="center" justify="space-between" gap={12}>
              <InlinePickerLabelStyled>
                {getOwnsText(fromName, owned.length, owned.filter(isInReview).length)}
              </InlinePickerLabelStyled>
              {owned.length > 0 && (
                <SbButton type="link" size="small" aria-expanded={showJds} onClick={() => setShowJds((v) => !v)}>
                  {showJds ? `${HIDE_JDS} ▴` : `${SHOW_JDS} ▾`}
                </SbButton>
              )}
            </Flex>
            {showJds && (
              <OwnedItemsListStyled data-testid="owned-list">
                {owned.map((r) => (
                  <CompactItemStyled key={r.job_id}>
                    <ItemLineStyled justify="space-between" align="center" gap={12}>
                      <ConflictProjectNameStyled>{r.title || r.sb_requisition_number}</ConflictProjectNameStyled>
                      <ConflictItemMetaStyled>
                        {r.sb_requisition_number || r.job_id}{isInReview(r) ? ' · in review' : ''}
                      </ConflictItemMetaStyled>
                    </ItemLineStyled>
                  </CompactItemStyled>
                ))}
              </OwnedItemsListStyled>
            )}
          </div>
        )}
        <NewOwnerField candidates={candidates} rows={owned} loading={loading} value={newOwner} onChange={setNewOwner}
          error={error} disabled={!from || transferring || (candidates && !ok)}
          helpBelow={!from ? PICK_CURRENT_FIRST : candidates ? getAllEligibleHelp(ok, total) : null} />
        <CarryChoice value={carry} onChange={setCarry} affectedNames={getAffectedNames(owned)} />
      </Flex>
    </SbModal>
  );
};

TransferEverythingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  transferring: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
};

export default TransferEverythingModal;
